import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ModalActionsComponent, PasslyCheckbox, PasslyInfiniteScroll, PasslyTextField } from '@app/components';

import { useTypedTranslation } from '@app/hooks';
import { getModalSwitch, getLoaderBtn } from '@app/store/actions';
import { vaultsService } from '@app/services';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { displayName, genericErrorToast, infiniteScrollItemsFetchLimit, localStore, useDebounce } from '@app/helpers';
import { IVaultsDisplay, Order } from '@app/models';
import { Box, CircularProgress, FormControl } from '@material-ui/core';
import { AxiosError, AxiosResponse } from 'axios';
import { darkwebService } from '@app/services';
import { toast } from 'react-toastify';
import { PasslyTooltip } from '@app/componentsV2';

interface IRowProps {
    displayName: string;
    checked: boolean;
    id: number;
    orgName: string;
}

export const ChooseVaultDialog = () => {
    const dispatch = useDispatch();
    const [totalCount, setTotalCount] = useState(0);
    const [displayText, setDisplayText] = useState('');
    const [rows, setRows] = useState<IRowProps[]>([]);
    const [resetScrollCount, setResetScrollCount] = useState(0);

    const [dataLoading, setDataLoading] = useState<boolean>(false);

    const { translation } = useTypedTranslation();
    const isButtonDisabled = !rows.filter((el: IRowProps) => {
        return el.checked;
    }).length;
    const debouncedValue = useDebounce(displayText, 500);
    const currentOrgId: string = localStore.getItem('org_filter') !== null ? localStore.getItem('org_filter')! : '';

    const handleFilteredList = (e: any) => {
        const value = e.target.value;

        setDisplayText(value);
    };

    const handleError = (error: AxiosError<any>) => {
        const respStatus = error.response?.status;
        const errorMessage = error.response?.data.error.message;
        setDataLoading(false);
        handleCancel();

        if (respStatus === 400 && errorMessage === 'ORG_CANNOT_VIEW') {
            toast.error(translation('DARKWEB_COMPROMISE_ORG_CANNOT_VIEW_ERROR'));
            return;
        }

        genericErrorToast(error);
    };

    const handleSuccess = (response: AxiosResponse) => {
        const results: IVaultsDisplay[] = response.data.value;
        const count = response.data['@odata.count'];

        count && setTotalCount(count);

        if (results) {
            let newRows = results.map((row: any) => {
                const data: any = {
                    checked: false,
                    id: row.Id,
                    cutId: `...${row.Id.slice(-12)}`,
                    displayName: row.DisplayName,
                    orgName: row.OrgName
                };

                return data;
            });
            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined && !rows.length) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setDataLoading(false);
    };

    const handleChooseVault = () => {
        const selectedItem = rows.find((el: IRowProps) => {
            return el.checked;
        });

        if (!selectedItem) {
            return;
        }

        dispatch(getLoaderBtn(true));

        darkwebService.enableScanning(selectedItem.id, handleError, handleStartScanSuccess);
    };

    const handleCancel = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
    };

    const handleStartScanSuccess = () => {
        handleCancel();
        dispatch(handleSetSnackbar({ show: true, message: translation('SCAN_HAS_STARTED'), severity: 'success' }));
    };

    const onCheckboxChange = (checked: boolean, e: any) => {
        const indexOfItem: number = rows.findIndex((item: IRowProps) => item.id === e.target.name);

        resetAllCheckboxes();

        if (indexOfItem !== -1) {
            let newRows: IRowProps[] = [...rows];

            newRows[indexOfItem].checked = checked;
            setRows(newRows);
        }
    };

    const resetAllCheckboxes = () => {
        if (rows.length > 0) {
            let newRows: IRowProps[] = [...rows];

            newRows.forEach((newRow) => {
                newRow.checked = false;
            });
            setRows(newRows);
        }
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        vaultsService.getModifiableVaults(
            displayName,
            Order.asc,
            infiniteScrollItemsFetchLimit,
            skipCount,
            currentOrgId,
            handleError,
            handleSuccess,
            isReset,
            true,
            debouncedValue
        );
    };

    useEffect(() => {
        if (!dataLoading) {
            resetAllCheckboxes();
            setResetScrollCount(resetScrollCount + 1);
        }
    }, [debouncedValue]);

    useEffect(() => {
        setRows([]);
    }, [displayText]);

    return (
        <>
            <div className='modal__identities-list' tabIndex={0}>
                <div className='modal__identities-list--filter modal__identities-list--filter-padding-bottom'>
                    <FormControl>
                        <PasslyTextField
                            fullWidth
                            id='modal-identities-list-filter'
                            label={'FILTER_BY'}
                            value={displayText}
                            onChange={handleFilteredList}
                            autoFocus
                            showSearchIcon={true}
                        />
                    </FormControl>
                </div>
                <PasslyInfiniteScroll totalCount={totalCount ?? 0} currentCount={rows.length} onFetch={fetchMoreData} resetLocal={resetScrollCount}>
                    <>
                        {rows.length
                            ? rows.map((item: any) => (
                                  <div className='modal__identities-list--row' key={item.id}>
                                      <PasslyCheckbox name={item.id} checked={item.checked} onChange={onCheckboxChange} label={''} />
                                      <Box ml={1}>{item.displayName}</Box>
                                      <div className='modal__identities-list--org'>
                                          ({item.orgName} -{' '}
                                          <PasslyTooltip title={item.id}>
                                              <span>{item.cutId}</span>
                                          </PasslyTooltip>
                                          )
                                      </div>
                                  </div>
                              ))
                            : null}
                    </>
                </PasslyInfiniteScroll>
                {!rows.length && dataLoading ? (
                    <CircularProgress size={64} className='modal__identities-list-loader' />
                ) : !rows.length ? (
                    <p className='modal__identities-list--no-data'>{translation('DARKWEB_COMPROMISE_MODAL_NO_VAULTS_TO_SCAN')}</p>
                ) : null}
            </div>
            <ModalActionsComponent
                primaryBtn={translation('DARKWEB_COMPROMISE_BUTTON_SCAN_NOW')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleChooseVault}
                onClickSecondary={handleCancel}
                disabled={isButtonDisabled}
                id='addRoles'
            />
        </>
    );
};
